import { __assign, __spreadArray } from "tslib";
// Components
import { Document16 } from '@yunex/yds-icons';
import { Box, GRID_ROW_HEIGHT, Spinner, TextFilter, rem, styled, useKeyPress, } from '@yunex/yds-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { default as isEqual } from 'fast-deep-equal/es6';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { FADE_IN_ANIMATION } from '../../../utils/consts';
import { useFocusWithin } from '../../../utils/hooks/useFocusWithin';
import ListViewContextWrapper from '../../common/ListViewContextWrapper';
import { CMP_LIST_TABLE as CMP } from '../../componentNames';
import { useGridOverlay } from '../useGridOverlay';
import { useListViewStoreGetter, useListViewStoreSetter } from '../useListView';
import ComponentOrEmpty from './ComponentOrEmpty';
import ContextMenu from './ContextMenu';
// Constants.
export var PREVENT_SELECTION_CLASS_NAME = 'yds-prevent-row-selection';
export var UNDEFINED_FILTER_MODEL = 'UNDEFINED_FILTER_MODEL'; // Represents a key in the filter model when user changes the filter by hand.
var COMPACT_SIZE_ROW_HEIGHT = 56;
var LoadingWrapper = styled('div')({
    display: 'grid',
    placeContent: 'center',
    height: '100%',
});
// Styles.
var styles = {
    root: function (isCompactList) { return ({
        height: 1,
        paddingBottom: 0,
        paddingLeft: isCompactList ? 0 : 4,
        paddingRight: isCompactList ? 0 : 4,
        width: '100%',
        '&.ag-theme-alpine': {
            '--ag-row-height': rem(isCompactList ? COMPACT_SIZE_ROW_HEIGHT : GRID_ROW_HEIGHT),
        },
        '& .ag-compact-size .ag-header': {
            display: 'none',
            height: '1px',
            minHeight: '1px',
        },
        '& .ag-compact-size .ag-row:first-of-type .ag-cell:not(.ag-cell-focus)': {
            borderTop: '1px solid var(--ag-row-border-bottom-color)',
        },
        '.ag-popup-child': {
            animation: FADE_IN_ANIMATION,
        },
    }); },
};
/*
 * For performance reasons, we can make custom hook, because we need columnStateFromStore only in one callback,
 * and we do want to component re-render where this state changes.
 */
var useOnGridReady = function (setColumnState) {
    var columnStateFromStore = useListViewStoreGetter(function (s) { return s.columnState; });
    return useCallback(function (e) {
        var colStateFromApi = e.columnApi.getColumnState();
        // We want to dispatch tableColumnState only if there is no existing settings in the persist store.
        if (columnStateFromStore.length !== 0) {
            e.columnApi.applyColumnState({ applyOrder: true, state: columnStateFromStore });
        }
        else {
            setColumnState(colStateFromApi);
        }
    }, []);
};
var ListTableComponent = function (props) {
    var _a, _b, _c;
    var _d = props.compactListColumn, compactListColumn = _d === void 0 ? undefined : _d, contextMenuItems = props.contextMenuItems, defaultColumnDefs = props.defaultColumnDefs, defaultColumns = props.defaultColumns, listData = props.listData, onContextMenuItemSelected = props.onContextMenuItemSelected, onFilterItemCountChange = props.onFilterItemCountChange, onFilterModelChange = props.onFilterModelChange, onSelectedDetailChange = props.onSelectedDetailChange, onSelectedItemsCountChange = props.onSelectedItemsCountChange, onSelectedItemsChange = props.onSelectedItemsChange, onTableSettingsColumnStateChanged = props.onTableSettingsColumnStateChanged, tableFilterModel = props.tableFilterModel, agGridProps = props.agGridProps, _e = props.preventSelectionOnContextMenu, preventSelectionOnContextMenu = _e === void 0 ? false : _e, _f = props.isLoading, isLoading = _f === void 0 ? false : _f, _g = props.loadingState, loadingState = _g === void 0 ? (React.createElement(LoadingWrapper, null,
        React.createElement(Spinner, { "aria-label": "Please wait..." }))) : _g, _h = props.noItemsState, noItemsState = _h === void 0 ? {
        icon: React.createElement(Document16, null),
    } : _h, isCompactSize = props.isCompactSize;
    var gridApi = useListViewStoreGetter(function (s) { return s.gridApi; });
    // const gridColumnApi = useListViewStoreGetter(s => s.gridColumnApi)
    var markedRowsCount = useListViewStoreGetter(function (s) { return s.markedRows.length; });
    var selectedRow = useListViewStoreGetter(function (s) { return s.selectedRow; });
    var isCompactListCtx = useListViewStoreGetter(function (s) { return s.isCompactList; });
    var autoColumnWidth = useListViewStoreGetter(function (s) { return s.autoColumnWidth; });
    var quickSearch = useListViewStoreGetter(function (s) { return s.quickSearch; });
    var setStore = useListViewStoreSetter();
    // If true comes from props, set compactList to true and do not use context value.
    var isCompactList = isCompactSize === undefined ? isCompactListCtx : isCompactSize;
    // For backward compatibility we can use callBacks "onXyChanged" but ofr better perf we should remove them - breaking change.
    var setFilteredRowsCount = useCallback(function (count) {
        onFilterItemCountChange === null || onFilterItemCountChange === void 0 ? void 0 : onFilterItemCountChange(count);
        setStore({ filteredRowsCount: count });
    }, [onFilterItemCountChange]);
    var setSelectedRowsCount = useCallback(function (nodes) {
        onSelectedItemsCountChange === null || onSelectedItemsCountChange === void 0 ? void 0 : onSelectedItemsCountChange(nodes.length);
        setStore({ markedRows: nodes });
    }, [onSelectedItemsCountChange]);
    var setSelectedRow = useCallback(function (row) {
        onSelectedDetailChange === null || onSelectedDetailChange === void 0 ? void 0 : onSelectedDetailChange(row);
        setStore({ selectedRow: row });
    }, [onSelectedDetailChange]);
    var setColumnState = useCallback(function (state) {
        onTableSettingsColumnStateChanged === null || onTableSettingsColumnStateChanged === void 0 ? void 0 : onTableSettingsColumnStateChanged(state);
        setStore({ columnState: state });
    }, [onTableSettingsColumnStateChanged]);
    var gridReadyCallback = useOnGridReady(setColumnState);
    // Default agGridProps
    var rowSelection = (agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.rowSelection) || 'multiple';
    /* -- LOCAL STATE -- */
    var _j = useState(null), anchorPoint = _j[0], setAnchorPoint = _j[1];
    var _k = useState(null), savedVisibleColumns = _k[0], setSavedVisibleColumns = _k[1];
    var _l = useState(false), showCtxMenu = _l[0], setShowCtxMenu = _l[1];
    var _m = useState(), focusedRow = _m[0], setFocusedRow = _m[1];
    var isShiftPressed = useKeyPress('Shift');
    var isCtrlPressed = useKeyPress('Control');
    var isEmpty = !((_a = listData === null || listData === void 0 ? void 0 : listData.items) === null || _a === void 0 ? void 0 : _a.length);
    useGridOverlay({ gridApi: gridApi, isLoading: isLoading, isEmpty: isEmpty });
    /* -- COMPUTED VALUES FROM PROPS - NOT A STATE! -- */
    // Dynamic column definitions. If columnDef changed from props, memo runs and set new columnDefs through gridApi.
    var columnDefs = useMemo(function () {
        return __spreadArray(__spreadArray([], defaultColumns.map(function (col) { return (__assign(__assign({}, col), { hide: isCompactList })); }), true), [
            __assign(__assign({}, compactListColumn), { hide: !isCompactList }),
        ], false);
    }, [defaultColumns, compactListColumn]);
    var defaultColDef = useMemo(function () {
        return __assign({ filter: TextFilter, flex: 1, getQuickFilterText: function (e) { return e.value; }, minWidth: 90, resizable: !autoColumnWidth, sortable: true, suppressKeyboardEvent: function (e) {
                var _a;
                if (e.event.key === 'Tab') {
                    // TODO - solve tab in and tab out to grid
                    var cellPosition = e.api.getFocusedCell();
                    if (cellPosition) {
                        e.api.setFocusedCell(cellPosition === null || cellPosition === void 0 ? void 0 : cellPosition.rowIndex, cellPosition === null || cellPosition === void 0 ? void 0 : cellPosition.column);
                        (_a = e.api) === null || _a === void 0 ? void 0 : _a.clearFocusedCell();
                    }
                    return true;
                }
                return false;
            } }, defaultColumnDefs);
    }, [defaultColumnDefs]);
    var _o = useFocusWithin(), focusWithinRef = _o.ref, isGridFocused = _o.focused;
    /* -- AG-GRID EVENTS -- */
    // This is needed for home/end key navigation.
    var onBodyScrollEnd = useCallback(function (e) {
        var _a;
        var focusedCell = e.api.getFocusedCell();
        if (focusedCell) {
            e.api.setFocusedCell(focusedCell === null || focusedCell === void 0 ? void 0 : focusedCell.rowIndex, focusedCell === null || focusedCell === void 0 ? void 0 : focusedCell.column);
        }
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onBodyScrollEnd) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onBodyScrollEnd]);
    var onCellContextMenu = useCallback(function (e) {
        var _a;
        var event = e.event, node = e.node;
        // @ts-ignore - ag-grid types are not correct
        setAnchorPoint({ x: event === null || event === void 0 ? void 0 : event.pageX, y: event === null || event === void 0 ? void 0 : event.pageY });
        setShowCtxMenu(true);
        setSelectedRow(e.data);
        if (rowSelection === 'single') {
            var row = e.api.getDisplayedRowAtIndex(e.rowIndex);
            row === null || row === void 0 ? void 0 : row.setSelected(true);
        }
        if (node === null || node === void 0 ? void 0 : node.data) {
            onContextMenuItemSelected === null || onContextMenuItemSelected === void 0 ? void 0 : onContextMenuItemSelected(node.data);
        }
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onCellContextMenu) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onCellContextMenu, onContextMenuItemSelected, rowSelection, setSelectedRow]);
    var onColumnResized = useCallback(function (e) {
        var _a;
        // When user manually resize colum width, set autoColumnWidth to false and change columns state.
        if (autoColumnWidth === true && e.source === 'uiColumnDragged' && e.finished) {
            var colState = e.columnApi.getColumnState();
            e.columnApi.applyColumnState({ applyOrder: true, state: colState });
        }
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onColumnResized) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [autoColumnWidth, agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onColumnResized]);
    var onDragStopped = useCallback(function (e) {
        var _a;
        var colState = e.columnApi.getColumnState();
        if (colState.length > 1 && !isCompactList) {
            setColumnState(colState);
        }
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onDragStopped) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onDragStopped, isCompactList]);
    var onFilterChanged = useCallback(function (e) {
        var _a, _b;
        var currentFilterModel = (_a = e.api) === null || _a === void 0 ? void 0 : _a.getFilterModel();
        var areModelsEqual = isEqual(currentFilterModel, tableFilterModel === null || tableFilterModel === void 0 ? void 0 : tableFilterModel.filters);
        // Get all selected rows from model and deselect rows that are not visible.
        e.api.forEachNode(function (node) {
            if (!node.displayed && node.isSelected()) {
                node.setSelected(false, false);
            }
        });
        var newSelectedNodes = e.api.getSelectedNodes();
        setSelectedRowsCount(newSelectedNodes);
        setFilteredRowsCount(e.api.getDisplayedRowCount());
        onSelectedItemsChange === null || onSelectedItemsChange === void 0 ? void 0 : onSelectedItemsChange(newSelectedNodes);
        // If user changed filter model, ie. he edited filters by hand, dispatch it to redux store.
        // TODO We can prevent dispatch when only quick filter has changed.
        if (!areModelsEqual && onFilterModelChange) {
            onFilterModelChange({
                filters: currentFilterModel,
                key: UNDEFINED_FILTER_MODEL,
            });
        }
        (_b = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onFilterChanged) === null || _b === void 0 ? void 0 : _b.call(agGridProps, e);
    }, [tableFilterModel === null || tableFilterModel === void 0 ? void 0 : tableFilterModel.filters, onFilterModelChange]);
    var handleRowDataUpdated = useCallback(function (e) {
        var _a;
        setFilteredRowsCount(e.api.getDisplayedRowCount());
        var newSelectedNodes = e.api.getSelectedNodes();
        setSelectedRowsCount(newSelectedNodes);
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onRowDataUpdated) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onRowDataUpdated]);
    var onGridReadyCall = useCallback(function (e) {
        var _a;
        setStore({
            gridApi: e.api,
            gridColumnApi: e.columnApi,
        });
        setFilteredRowsCount(e.api.getDisplayedRowCount());
        gridReadyCallback(e);
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onGridReady) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onGridReady, isCompactList, gridReadyCallback]);
    var handleFirstDataRendered = useCallback(function (e) {
        var _a;
        setTimeout(function () {
            e.api.sizeColumnsToFit();
        }, 250);
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onFirstDataRendered) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onFirstDataRendered]);
    var prevCompactSize = useRef(isCompactList);
    var onGridSizeChanged = useCallback(function (e) {
        var _a;
        var colApi = e.columnApi;
        var newCols = colApi.getColumnState();
        var isCompactListChanged = prevCompactSize.current !== isCompactList;
        if (isCompactListChanged) {
            // Save previous visible columns so ve can restore them when compact size is disabled.
            if (isCompactList) {
                setSavedVisibleColumns(colApi.getAllDisplayedColumns().map(function (c) { return c.getColId(); }));
            }
            newCols = newCols.map(function (column) {
                if (column.colId === 'compactList') {
                    return __assign(__assign({}, column), { hide: !isCompactList });
                }
                var isInHiddenCols;
                // If table starts as a compactList, we want to show all columns after switching to normal size.
                if (savedVisibleColumns === null) {
                    isInHiddenCols = false;
                }
                else {
                    isInHiddenCols = !savedVisibleColumns.includes(column.colId);
                }
                return __assign(__assign({}, column), { hide: isCompactList ? true : isInHiddenCols });
            });
            prevCompactSize.current = isCompactList;
        }
        if (autoColumnWidth === true) {
            window.requestAnimationFrame(function () {
                var _a;
                (_a = e.api) === null || _a === void 0 ? void 0 : _a.sizeColumnsToFit();
            });
        }
        if (autoColumnWidth === true || isCompactListChanged) {
            colApi.applyColumnState({ applyOrder: true, state: newCols });
        }
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onGridSizeChanged) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [autoColumnWidth, agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onGridSizeChanged, isCompactList]);
    var onSelectionChanged = useCallback(function (e) {
        var _a;
        setSelectedRowsCount(e.api.getSelectedNodes());
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onSelectionChanged) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onSelectionChanged]);
    var onSortChanged = useCallback(function (e) {
        var _a, _b;
        var colState = (_a = e.columnApi) === null || _a === void 0 ? void 0 : _a.getColumnState();
        if (colState) {
            setColumnState(colState);
        }
        // Move focus to previous selected row
        var cell = e.api.getFocusedCell();
        e.api.clearFocusedCell();
        if (cell && (focusedRow === null || focusedRow === void 0 ? void 0 : focusedRow.rowIndex)) {
            e.api.setFocusedCell(focusedRow === null || focusedRow === void 0 ? void 0 : focusedRow.rowIndex, cell.column);
            e.api.ensureIndexVisible(focusedRow === null || focusedRow === void 0 ? void 0 : focusedRow.rowIndex, 'middle');
        }
        (_b = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onSortChanged) === null || _b === void 0 ? void 0 : _b.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onSortChanged, focusedRow === null || focusedRow === void 0 ? void 0 : focusedRow.rowIndex]);
    /* -- METHODS / FUNCTIONS -- */
    var handleCloseContextMenu = useCallback(function () {
        setShowCtxMenu(false);
        setAnchorPoint(null);
        if (!gridApi) {
            return;
        }
        var cell = gridApi.getFocusedCell();
        if (cell) {
            gridApi.setFocusedCell(cell.rowIndex, cell.column);
        }
    }, [gridApi !== undefined]);
    useEffect(function () {
        var _a;
        if (!gridApi) {
            return;
        }
        var filterModel = gridApi === null || gridApi === void 0 ? void 0 : gridApi.getFilterModel();
        // Go through all columns and set resizable based on autoColumnWidth.
        var newColDef = (_a = gridApi === null || gridApi === void 0 ? void 0 : gridApi.getColumnDefs()) === null || _a === void 0 ? void 0 : _a.map(function (column) {
            column.resizable = !autoColumnWidth;
            return column;
        });
        if (newColDef) {
            gridApi.setColumnDefs(newColDef);
            // This is required because setColumnDefs resets filter model.
            gridApi.setFilterModel(filterModel);
        }
        if (autoColumnWidth === true) {
            gridApi === null || gridApi === void 0 ? void 0 : gridApi.sizeColumnsToFit();
        }
    }, [autoColumnWidth]);
    // Apply filter to ag-grid filter model based on tableFilterModel from redux.
    // Key has to be changed to apply filters to grid.
    useEffect(function () {
        if (gridApi && tableFilterModel && tableFilterModel.key !== UNDEFINED_FILTER_MODEL) {
            gridApi.setFilterModel(tableFilterModel.filters);
            setTimeout(function () {
                gridApi === null || gridApi === void 0 ? void 0 : gridApi.onFilterChanged();
            });
        }
    }, [gridApi !== undefined, tableFilterModel === null || tableFilterModel === void 0 ? void 0 : tableFilterModel.key]);
    var handleCellFocused = useCallback(function (e) {
        if (e.rowIndex === null) {
            return;
        }
        var currentRow = e.api.getDisplayedRowAtIndex(e.rowIndex);
        if (currentRow) {
            // Use callback to get previous focused row, so we do not have to take it from state and extend callback dependencies.
            setFocusedRow(function (prev) {
                var _a;
                if (rowSelection === 'multiple' &&
                    isShiftPressed &&
                    (prev === null || prev === void 0 ? void 0 : prev.rowIndex) &&
                    (currentRow === null || currentRow === void 0 ? void 0 : currentRow.rowIndex)) {
                    // Select all rows between previously focused and currently focused row.
                    var start = Math.min(prev.rowIndex, currentRow.rowIndex);
                    var end = Math.max(prev.rowIndex, currentRow.rowIndex);
                    // Select all rows between selected and clicked row.
                    for (var i = start; i <= end; i++) {
                        // Thanks to last parameter, selectionChanged event will be fired only once. (perf improvement)
                        (_a = e.api.getDisplayedRowAtIndex(i)) === null || _a === void 0 ? void 0 : _a.setSelected(true, false);
                    }
                }
                return currentRow;
            });
        }
    }, [isShiftPressed, rowSelection]);
    var handleNavigateToNextCell = useCallback(function (e) {
        var _a, _b, _c, _d;
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.navigateToNextCell) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
        var suggestedNextCell = e.nextCellPosition;
        var prevCell = e.previousCellPosition;
        if (suggestedNextCell && ((_b = e.event) === null || _b === void 0 ? void 0 : _b.shiftKey)) {
            // If shift is pressed, we need to select previous and next row.
            (_c = e.api.getDisplayedRowAtIndex(prevCell.rowIndex)) === null || _c === void 0 ? void 0 : _c.setSelected(true);
            (_d = e.api.getDisplayedRowAtIndex(suggestedNextCell.rowIndex)) === null || _d === void 0 ? void 0 : _d.setSelected(true);
        }
        // if ('ontouchstart' in window === false) {
        // Get next cell data and set it as selected row.
        var nextRow = e.api.getDisplayedRowAtIndex((suggestedNextCell === null || suggestedNextCell === void 0 ? void 0 : suggestedNextCell.rowIndex) || 0);
        if (nextRow) {
            setSelectedRow(nextRow.data);
            if (rowSelection === 'single') {
                nextRow.setSelected(true);
            }
        }
        // }
        return suggestedNextCell;
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.navigateToNextCell, rowSelection]);
    var handleRowClicked = useCallback(function (e) {
        var _a, _b;
        var target = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.target;
        // Workaround with selector, because rowClicked event does not have an information about column.
        if (!target) {
            return;
        }
        var preventSelection = target.closest(".".concat(PREVENT_SELECTION_CLASS_NAME));
        if (!preventSelection) {
            setSelectedRow(e.data);
            if (rowSelection === 'single' && preventSelectionOnContextMenu === false) {
                var row = e.api.getDisplayedRowAtIndex(e.rowIndex);
                row === null || row === void 0 ? void 0 : row.setSelected(true);
            }
        }
        (_b = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onRowClicked) === null || _b === void 0 ? void 0 : _b.call(agGridProps, e);
    }, [agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onRowClicked, rowSelection]);
    // In single row selection, we handle selection by ourselves.
    var suppressRowClickSelection = useMemo(function () {
        if (rowSelection === 'single') {
            return true;
        }
        else
            return !isShiftPressed && markedRowsCount === 0 && !isCtrlPressed;
    }, [isCtrlPressed, isShiftPressed, rowSelection, markedRowsCount]);
    // When column is removed or added, we need to fit columns to grid.
    var handleColumnVisible = useCallback(function (e) {
        var _a;
        if (autoColumnWidth === true) {
            e.api.sizeColumnsToFit();
        }
        (_a = agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onColumnVisible) === null || _a === void 0 ? void 0 : _a.call(agGridProps, e);
    }, [autoColumnWidth, agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.onColumnVisible]);
    return (React.createElement(Box, { className: clsx('ag-theme-alpine', isCompactList && 'ag-compact-size'), "data-testid": "".concat(CMP, "-root"), onContextMenu: function (e) { return e.preventDefault(); }, ref: focusWithinRef, sx: styles.root(isCompactList) },
        React.createElement(AgGridReact, __assign({ defaultColDef: defaultColDef, loadingOverlayComponent: ComponentOrEmpty, loadingOverlayComponentParams: { componentOrProps: loadingState }, noRowsOverlayComponent: ComponentOrEmpty, noRowsOverlayComponentParams: { componentOrProps: noItemsState }, quickFilterText: quickSearch, rowBuffer: 30, rowData: (listData === null || listData === void 0 ? void 0 : listData.items) || [], tooltipShowDelay: 300 }, agGridProps, { className: clsx(agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.className, isCompactList && 'ag-compact-size', isGridFocused ? 'yds-grid-focused' : 'yds-grid-not-focused', markedRowsCount > 0 && 'ag-show-checkboxes'), columnDefs: columnDefs, 
            // Handle shift + arrow selection
            navigateToNextCell: handleNavigateToNextCell, onBodyScrollEnd: onBodyScrollEnd, onCellContextMenu: onCellContextMenu, onCellFocused: handleCellFocused, onColumnResized: onColumnResized, onColumnVisible: handleColumnVisible, onDragStopped: onDragStopped, onFilterChanged: onFilterChanged, onFirstDataRendered: handleFirstDataRendered, onGridReady: onGridReadyCall, onGridSizeChanged: onGridSizeChanged, onRowClicked: handleRowClicked, onRowDataUpdated: handleRowDataUpdated, onSelectionChanged: onSelectionChanged, onSortChanged: onSortChanged, paginationPageSize: (_c = (_b = listData === null || listData === void 0 ? void 0 : listData.size) !== null && _b !== void 0 ? _b : agGridProps === null || agGridProps === void 0 ? void 0 : agGridProps.paginationPageSize) !== null && _c !== void 0 ? _c : 100, 
            // Thanks to this users will be able to select items with simle click when suppressRowClickSelection is false.
            rowMultiSelectWithClick: rowSelection === 'multiple' && !isShiftPressed, rowSelection: rowSelection, 
            // If one or more row is selected this needs to be false. Or if ctrl or shift is pressed we want to enable row click selection.
            suppressRowClickSelection: suppressRowClickSelection })),
        contextMenuItems && contextMenuItems.length > 0 && (React.createElement(ContextMenu, { anchorPoint: anchorPoint, isShown: showCtxMenu, items: contextMenuItems, onClose: handleCloseContextMenu, selectedDetail: selectedRow }))));
};
var makeErrorInConsole = function (propName, componentName) {
    console.error("[YDS-REACT-COMPOSITES]: The prop `".concat(propName, "` is deprecated in `").concat(componentName, "`. Please use `useListView()` hook instead."));
};
var propertiesToCheck = [
    'quickSearch',
    'autoColumnWidth',
    'selectedItemsCount',
    'onTableSettingsAutoColumnWidthChange',
];
var ListTable = function (props) {
    if (process.env.NODE_ENV !== 'production') {
        propertiesToCheck.forEach(function (property) {
            if (props === null || props === void 0 ? void 0 : props[property]) {
                makeErrorInConsole(property, CMP);
            }
        });
        if (props === null || props === void 0 ? void 0 : props.tableColumnState) {
            console.error("[YDS-REACT-COMPOSITES]: The prop `tableColumnState` is deprecated in `".concat(CMP, "`. To set tableColumnState use gridApi.setColumnState() instead."));
        }
    }
    return (React.createElement(ListViewContextWrapper, null,
        React.createElement(ListTableComponent, __assign({}, props))));
};
export default ListTable;
//# sourceMappingURL=ListTable.js.map